import {forwardRef} from "react";
import type {ReactNode, CSSProperties} from "react";
import type {ItemVariantEntry} from "../../models/GroupRepo";
import {StorageKey, storageWrapper} from "../../storage";
import {transitions} from "../../styles/decoration.css";
import {blueTheme, darkPillTheme, greenTheme} from "../../styles/themes/color-themes.css";
import {Box, Col, css, Row, Text} from "../../ui/Box";
import type {IconName} from "../../ui/Icon";
import Icon from "../../ui/Icon";
import {prettyNumber} from "../../utils";
import useAnimationOnClick from "../../utils/use-animation-on-click";
import useLongPress from "../../utils/use-long-press";
import {pillStyles as styles} from "./ItemPill.css";

export const ItemPillGrid = ({children}: {children: ReactNode}) => {
  const pillSpacing = storageWrapper.storageGet(StorageKey.PILL_SPACING) || "wide";
  return (
    <Box
      display="grid"
      sp="4"
      gridTemplateColumns={pillSpacing === "compact" ? "_itemPillGridCompact" : "_itemPillGridWide"}
    >
      {children}
    </Box>
  );
};

// prevent image download overlay on anrdroid
document.addEventListener("contextmenu", (event) => {
  event.preventDefault();
  event.stopPropagation();
});

type ConfirmAnimation = "done" | "added";

const AnimIcon = ({iconName, theme}: {iconName: IconName; theme: string}) => (
  <>
    <Col
      className={css({}, theme, styles.checkboxArea)}
      absolute
      inset="0"
      bg="surface"
      zIndex="1"
      pointerEvents="none"
    />
    <Col
      absolute
      inset="0"
      align="center"
      justify="center"
      zIndex="1"
      className={theme}
      color="primary"
      pointerEvents="none"
    >
      <Icon name={iconName} size="24" className={styles.checkbox} />
    </Col>
  </>
);

type SharedItemPillProps = {
  onClick?: () => void;
  onLongClick?: () => void;
  confirmAnimation?: ConfirmAnimation;
};

type ItemPillLayoutProps = SharedItemPillProps & {
  name: ReactNode;
  theme: string;
  emoji: string | null | ReactNode;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
};
export const ItemPillLayout = forwardRef<HTMLDivElement, ItemPillLayoutProps>((props, ref) => {
  const {
    children,
    onClick: rawOnClick,
    onLongClick,
    emoji,
    theme,
    name,
    confirmAnimation,
    style,
    className,
  } = props;

  const {onClick, animClass} = useAnimationOnClick({
    disabled: !confirmAnimation,
    onClick: rawOnClick,
  });

  const clickHandlers = useLongPress({onClick, onLongClick});

  return (
    <div
      {...clickHandlers}
      className={css(
        {
          display: "flex",
          bg: "surface",
          elevation: "100",
          cursor: rawOnClick ? "pointer" : undefined,
          userSelect: "none",
          rounded: "4",
          overflow: "hidden",
          position: "relative",
          height: "48px",
        },
        transitions.transformAndColors,
        animClass,
        className
      )}
      style={style}
      ref={ref}
    >
      {confirmAnimation === "done" && (
        <AnimIcon iconName="check" theme={animClass ? greenTheme : theme} />
      )}
      {confirmAnimation === "added" && (
        <AnimIcon iconName="plus" theme={animClass ? blueTheme : theme} />
      )}
      <Row
        className={css(
          {
            flex: "none",
            bg: "surface",
            color: "primary",
            textAlign: "center",
            size: "24",
            align: "center",
            justify: "center",
            width: "48px",
          },
          theme
        )}
      >
        {emoji}
      </Row>
      <Text
        px="16"
        size="20"
        color="primary"
        minWidth="0"
        flex="auto"
        alignSelf="center"
        whiteSpace="nowrap"
        className={styles.contentArea}
      >
        {name}
      </Text>
      {children}
    </div>
  );
});

const Pill = ({children}: {children: ReactNode}) => (
  <Box
    px="4"
    bg="surface"
    size="12"
    bold
    color="primary"
    className={darkPillTheme}
    rounded="4"
    textWrap="nowrap"
  >
    {children}
  </Box>
);
export const QuantityPill = ({variant}: {variant: Pick<ItemVariantEntry, "quantity" | "unit">}) => {
  if (variant.quantity === 1 && !variant.unit) return null;
  return (
    <Pill>
      {prettyNumber(variant.quantity)}
      {variant.unit ? ` ${variant.unit}` : ""}
    </Pill>
  );
};

export const MiniPill = Pill;
